import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useSeo from "@/lib/useSeo";
import {
  ArrowRight, Sparkles, Building2, GraduationCap, BookOpenCheck,
  PhoneOutgoing, Bot, BarChart3,
} from "lucide-react";

const FEATURES = [
  { icon: Building2, title: "Administration", body: "Manage teachers, students, classes, sections, subjects, timetables, and school operations from one powerful dashboard." },
  { icon: BookOpenCheck, title: "Teacher Portal", body: "Take attendance, manage classes, assign homework, track student progress, and communicate effortlessly." },
  { icon: GraduationCap, title: "Student Portal", body: "Access homework, AI Tutor, study materials, attendance, results, and personalized learning anytime." },
  { icon: PhoneOutgoing, title: "AI Voice Automation", body: "Automate attendance alerts, birthday wishes, announcements, PTM reminders, and emergency notifications." },
  { icon: Bot, title: "AI Tutor", body: "Instant AI-powered learning assistance for doubts, explanations, practice questions, and personalized guidance." },
  { icon: BarChart3, title: "Smart Analytics", body: "Monitor attendance, academic performance, and school insights with real-time dashboards." },
];

export default function Welcome() {
  const { user, student, loading } = useAuth();
  useSeo({
    title: "Trishul High School — Smart School Management & AI Campus",
    description: "Trishul High School & Trishul AI Campus: a unified portal for attendance, exam results, timetables, homework, parent voice-call updates and an AI tutor for students.",
    path: "/",
  });
  if (!loading && user) {
    const next = user.is_dev_admin
      ? "/devadmin/users"
      : user.role === "utility_admin"
      ? "/utility/settings"
      : (user.role === "admin" || user.role === "super_admin" ? "/admin/dashboard" : "/dashboard");
    return <Navigate to={next} replace />;
  }
  if (!loading && student) {
    return <Navigate to="/student/dashboard" replace />;
  }

  return (
    <div className="relative min-h-screen overflow-hidden text-white bg-slate-950">
      <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div aria-hidden className="absolute -top-32 -left-32 h-[34rem] w-[34rem] rounded-full bg-sky-500/25 blur-[120px]" />
      <div aria-hidden className="absolute -bottom-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-indigo-500/20 blur-[140px]" />
      <div aria-hidden className="absolute top-1/3 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-[120px]" />

      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Top brand */}
        <header className="px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/trishul-logo.png" alt="Trishul High School" className="h-10 w-10 rounded-full bg-white" />
            <div>
              <div className="font-semibold leading-tight" style={{ fontFamily: "Outfit" }}>Trishul High School</div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/60">School ERP</div>
            </div>
          </div>
          <Link
            to="/login"
            className="hidden sm:inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur transition-colors text-sm font-medium border border-white/10"
            data-testid="welcome-header-signin"
          >
            Sign in <ArrowRight className="h-4 w-4" />
          </Link>
        </header>

        {/* Hero */}
        <main className="flex items-center px-6 sm:px-10 lg:px-16 pt-8 pb-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs text-white/85 mb-6 border border-white/15" data-testid="welcome-tag">
              <Sparkles className="h-3.5 w-3.5 text-sky-300" />
              AI-Powered School ERP Platform
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-4" style={{ fontFamily: "Outfit" }} data-testid="welcome-title">
              Welcome to <span className="text-sky-300">Trishul AI Campus</span>
            </h1>
            <p className="text-lg sm:text-xl font-medium text-white/90 mb-4" style={{ fontFamily: "Outfit" }}>
              Complete School Management. Powered by AI.
            </p>
            <p className="text-base sm:text-lg text-white/75 max-w-2xl mb-9">
              A unified platform for administrators, teachers, students, and parents to manage
              academics, communication, attendance, and AI-powered learning—all from one secure system.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/login"
                data-testid="welcome-cta-signin"
                className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-sky-500 hover:bg-sky-600 transition-colors font-medium shadow-lg shadow-sky-900/30"
              >
                Get Started <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#features"
                data-testid="welcome-explore-features"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur border border-white/15 transition-colors font-medium"
              >
                Explore Features
              </a>
            </div>
          </div>
        </main>

        {/* Feature grid */}
        <section id="features" className="px-6 sm:px-10 lg:px-16 pb-10 pt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-6xl">
            {FEATURES.map((f) => (
              <FeatureCard key={f.title} icon={f.icon} title={f.title} body={f.body} />
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-white/70" data-testid="welcome-footer">
            <span className="font-medium text-white/85">Empowering Smarter Schools with AI</span>
            <span className="text-white/25">·</span>
            <span>Secure</span><span className="text-white/25">•</span>
            <span>AI Powered</span><span className="text-white/25">•</span>
            <span>Cloud Ready</span><span className="text-white/25">•</span>
            <span>Mobile Friendly</span>
          </div>
          <div className="mt-3 text-xs text-white/45">
            © {new Date().getFullYear()} Trishul High School · Built by <a
              href="https://zerolabsdigi.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/45 hover:text-sky-300 cursor-pointer transition-colors"
              data-testid="zerolabs-link"
            >Zerolabs Digitals</a>
          </div>
        </section>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="group rounded-2xl bg-white/8 backdrop-blur-md border border-white/15 p-5 hover:bg-white/12 hover:border-sky-400/30 transition-colors">
      <div className="h-10 w-10 rounded-xl bg-sky-500/15 border border-sky-400/20 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
        <Icon className="h-5 w-5 text-sky-300" />
      </div>
      <div className="font-semibold mb-1.5" style={{ fontFamily: "Outfit" }}>{title}</div>
      <p className="text-sm text-white/70 leading-relaxed">{body}</p>
    </div>
  );
}
